<template>
  <el-tabs v-model="activeTab">
    <el-tab-pane label="儀器套用" name="instrumentMapping">
      <div>
        <!-- 頂部：選擇專案 + Cascader 篩選儀器 -->
        <el-card class="mb-16">
          <div class="flex-row align-center" style="gap: 16px; flex-wrap: wrap;">
            <div>
              <label class="label">{{ $t('project.pick') }}</label>
              <el-select
                v-model="form.project"
                filterable
                :placeholder="$t('project.pick')"
                @change="onProjectChange"
              >
                <el-option
                  v-for="p in projectList"
                  :key="p._id"
                  :value="p._id"
                  :label="p.project_id"
                />
              </el-select>
            </div>
            <div>
              <label class="label">{{ $t('chart.instrument.pick') }}</label>
              <el-cascader
                v-model="form.instrument"
                style="min-width: 200px"
                :options="projectOptions"
                :props="cascaderProps"
                :collapse-tags="true"
                :show-all-levels="false"
                :placeholder="$t('chart.instrument.pick')"
                @change="onCascaderChange"
              />
            </div>
          </div>
        </el-card>

        <el-card v-if="form.project" v-loading="loading">
          <!-- 批量操作列 -->
          <div class="flex-row align-center mb-16" style="gap: 8px;" v-if="selectedRows.length > 0">
            <span style="color: #606266; font-size: 13px;">已選 {{ selectedRows.length }} 筆，套用群組：</span>
            <el-select
              v-model="batchTargetsGroup"
              clearable
              placeholder="選擇 Alert Group"
              style="width: 260px"
            >
              <el-option
                v-for="g in alertGroupList"
                :key="g._id"
                :value="g._id"
                :label="g.notes || g._id"
              />
            </el-select>
            <el-button
              type="primary"
              :loading="batchSaving"
              @click="batchSave"
            >
              批量儲存
            </el-button>
          </div>

          <el-table
            ref="table"
            :data="rows"
            border
            style="width: 100%"
            empty-text="此專案無使用新版告警機制的儀器"
            @selection-change="onSelectionChange"
          >
            <el-table-column type="selection" width="45" />
            <el-table-column prop="instrumentName" label="儀器名稱" min-width="160" />
            <el-table-column prop="rule" label="Rule" width="100" />
            <el-table-column label="啟用" width="80" align="center">
              <template slot-scope="scope">
                <el-tag :type="scope.row.active ? 'success' : 'danger'" size="small">
                  {{ scope.row.active ? 'ON' : 'OFF' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="Target Group" min-width="260">
              <template slot-scope="scope">
                <el-select
                  v-model="scope.row.targetsGroup"
                  clearable
                  placeholder="選擇 Alert Group"
                  style="width: 100%"
                >
                  <el-option
                    v-for="g in alertGroupList"
                    :key="g._id"
                    :value="g._id"
                    :label="g.notes || g._id"
                  />
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="" width="80" align="center">
              <template slot-scope="scope">
                <el-button
                  type="primary"
                  size="mini"
                  :loading="scope.row.saving"
                  @click="saveRow(scope.row)"
                >
                  儲存
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </div>
    </el-tab-pane>

  </el-tabs>
</template>

<script>
export default {
  data() {
    return {
      activeTab: 'instrumentMapping',
      form: {
        project: '',
        instrument: [],
      },
      cascaderProps: {
        multiple: true,
        label: 'label',
        value: 'value',
        children: 'children',
      },
      projectOptions: [],
      instrumentMap: {},
      loading: false,
      alertGroupList: [],
      allRows: [],
      rows: [],
      selectedRows: [],
      batchTargetsGroup: null,
      batchSaving: false,
    };
  },
  computed: {
    projectList() {
      return this.$store.getters['project/projectList'];
    },
    selectedProject() {
      return this.$store.getters['project/selectedProject'];
    },
  },
  async mounted() {
    this.form.project = this.selectedProject?._id || '';
    await this.fetchAlertGroups();
    if (this.form.project) {
      await Promise.all([this.initCascaderSelector(), this.fetchInstruments()]);
    }
  },
  methods: {
    async onProjectChange() {
      this.form.instrument = [];
      this.allRows = [];
      this.rows = [];
      await Promise.all([this.initCascaderSelector(), this.fetchInstruments()]);
    },

    initCascaderSelector() {
      this.projectOptions = [];
      this.instrumentMap = {};
      return this.$axios({
        category: 'project',
        method: 'get',
        apiName: `${this.form.project}/area-station-instrument`,
      }).then(res => {
        this.projectOptions = res.data.area.map(area => ({
          label: area.name,
          disabled: area.station.length === 0,
          children: area.station.map(station => ({
            label: station.name,
            disabled: station.instrument.length === 0,
            children: station.instrument.map(instrument => {
              this.instrumentMap[instrument._id] = instrument;
              return {
                label: instrument.latestVersion.name,
                value: instrument._id,
              };
            }),
          })),
        }));
      }).catch(e => {
        console.error(e);
        this.$notifyError(this.$t('chart.error.loading'));
      });
    },

    onCascaderChange() {
      if (!this.form.instrument.length) {
        this.rows = this.allRows;
        return;
      }
      const selectedIds = new Set(this.form.instrument.map(path => path[2]));
      this.rows = this.allRows.filter(row => selectedIds.has(row.instrumentId));
    },

    async fetchAlertGroups() {
      this.loading = true;
      try {
        const res = await this.$axios({
          category: 'alerts',
          method: 'get',
          apiName: 'getAllAlertGroup',
        });
        this.alertGroupList = Array.isArray(res.data) ? res.data : [];
      } catch(e) {
        this.$message.error('載入告警群組失敗');
      } finally {
        this.loading = false;
      }
    },

    async fetchInstruments() {
      this.loading = true;
      this.allRows = [];
      this.rows = [];
      try {
        const res = await this.$store.dispatch('data/getProjectInstrumentListWithLatestVersion', {
          projectId: this.form.project,
          isUpdate: true,
        });
        this.allRows = this.buildRows(res);
        this.rows = this.allRows;
      } catch(e) {
        this.$message.error('載入儀器失敗');
      } finally {
        this.loading = false;
      }
    },

    buildRows(instrumentList) {
      const rows = [];
      for (const ins of instrumentList) {
        if (!Array.isArray(ins.advanced_alert) || !ins.advanced_alert.length) continue;
        for (let i = 0; i < ins.advanced_alert.length; i++) {
          const rule = ins.advanced_alert[i];
          rows.push({
            instrumentId: ins._id,
            instrumentName: ins.latestVersion?.name || ins._id,
            ruleIndex: i,
            rule: rule.rule,
            active: rule.active,
            targetsGroup: rule.targetsGroup || null,
            _rawAdvancedAlert: ins.advanced_alert,
            saving: false,
          });
        }
      }
      return rows;
    },

    onSelectionChange(selection) {
      this.selectedRows = selection;
    },

    async saveRow(row) {
      row.saving = true;
      try {
        await this.submitRow(row);
        this.$message.success(`${row.instrumentName} 已儲存`);
      } catch(e) {
        this.$message.error('儲存失敗');
      } finally {
        row.saving = false;
      }
    },

    async batchSave() {
      if (!this.selectedRows.length) return;
      this.batchSaving = true;
      try {
        this.selectedRows.forEach(row => {
          row.targetsGroup = this.batchTargetsGroup;
        });

        const byInstrument = {};
        for (const row of this.selectedRows) {
          if (!byInstrument[row.instrumentId]) {
            byInstrument[row.instrumentId] = row._rawAdvancedAlert.map(item => ({ ...item }));
          }
          byInstrument[row.instrumentId][row.ruleIndex].targetsGroup = row.targetsGroup;
        }

        await Promise.all(
          Object.entries(byInstrument).map(([instrumentId, advanced_alert]) =>
            this.$axios({
              category: 'admin',
              method: 'put',
              apiName: `instrument/${instrumentId}/advanced-alert`,
              data: { advanced_alert },
            }),
          ),
        );

        for (const row of this.selectedRows) {
          row._rawAdvancedAlert = byInstrument[row.instrumentId];
        }

        const updatedCount = this.selectedRows.length;
        this.$refs.table.clearSelection();
        this.$message.success(`已批量更新 ${updatedCount} 筆`);
        this.batchTargetsGroup = null;
      } catch(e) {
        this.$message.error('批量儲存失敗');
      } finally {
        this.batchSaving = false;
      }
    },

    async submitRow(row) {
      const updatedAdvancedAlert = row._rawAdvancedAlert.map((item, idx) =>
        idx === row.ruleIndex ? { ...item, targetsGroup: row.targetsGroup } : item,
      );
      await this.$axios({
        category: 'admin',
        method: 'put',
        apiName: `instrument/${row.instrumentId}/advanced-alert`,
        data: { advanced_alert: updatedAdvancedAlert },
      });
      row._rawAdvancedAlert = updatedAdvancedAlert;
    },

  },
};
</script>

<style scoped>
.mb-16 { margin-bottom: 16px; }
.label { display: block; margin-bottom: 8px; font-weight: bold; color: #606266; font-size: 14px; }
.flex-row { display: flex; flex-direction: row; }
.align-center { align-items: center; }
</style>
